import { AxiosInstance, CancelTokenSource } from 'axios';

import * as common from '../../../../../../typings/api/skymap/rest/v0/.common';
import * as types from '../../../../../../typings/api/skymap/rest/v0/project';

export class ProjectService implements types.Handlers {
  constructor(
    private axios: AxiosInstance,
    private cancelTokenSource?: CancelTokenSource,
  ) {}
  async getProjectUserTagsV1(
    params: types.GetProjectUserTagsV1Parameters,
  ): Promise<types.GetProjectUserTagsV1Response> {
    return (
      await this.axios.get(`/projects/${params.path.projectId}/users/${params.path.userId}/tags-v1`)
    ).data;
  }
  async putProjectUserTagsV1(
    params: types.PutProjectUserTagsV1Parameters,
  ): Promise<types.GetProjectUserTagsV1Response> {
    return (
      await this.axios.put(
        `/projects/${params.path.projectId}/users/${params.path.userId}/tags-v1`,
        params.body,
      )
    ).data;
  }

  async getAll(params: types.GetAllParameters): Promise<types.GetAllProjectsResponse> {
    return (await this.axios.get(`/projects`)).data;
  }

  async getBasicProjectInformationList(): Promise<types.GetBasicProjectInformationListResponse> {
    return (await this.axios.get(`/projects/list`)).data;
  }

  async create(params: types.CreateParameters): Promise<types.CreateProjectResponse> {
    const { data } = await this.axios.post<types.CreateProjectResponse>('/projects', params.body, {
      cancelToken: this.cancelTokenSource?.token,
    });
    return data;
  }
  async update(params: types.UpdateParameters): Promise<types.SingleProjectResponse> {
    const { data } = await this.axios.put<types.SingleProjectResponse>(
      `/projects/${params.path.id}`,
      params.body,
    );
    return data;
  }

  async fetchProjectsGeodata(
    params: types.FetchProjectsGeodataParameters,
  ): Promise<types.FetchGeodataResponse> {
    return (
      await this.axios.get(`/projects/${params.path.projectId}/geodata`, {
        cancelToken: this.cancelTokenSource?.token,
      })
    ).data;
  }
  async getProjectAssets(
    params: types.GetProjectAssetsParameters,
  ): Promise<types.GetProjectAssetsResponse> {
    return (await this.axios.get(`/projects/${params.path.projectId}/assets`)).data;
  }
  async getProjectModels(
    params: types.GetProjectModelsParameters,
  ): Promise<types.GetProjectModelsResponse> {
    return (await this.axios.get(`/projects/${params.path.projectId}/models`)).data;
  }
  async getScans(params: types.GetScansParameters): Promise<types.GetProjectScansResponse> {
    return (await this.axios.get(`/projects/${params.path.projectId}/scans`)).data;
  }
  async getOrthoTiles(
    params: types.GetOrthoTilesParameters,
  ): Promise<types.GetProjectOrthoTilesResponse> {
    return (await this.axios.get(`/projects/${params.path.projectId}/ortho-tiles`)).data;
  }
  async getPointClouds(
    params: types.GetPointCloudsParameters,
  ): Promise<types.GetProjectPointCloudsResponse> {
    return (await this.axios.get(`/projects/${params.path.projectId}/point-clouds`)).data;
  }

  async getTerrainModels(
    params: types.GetTerrainModelsParameters,
  ): Promise<types.GetProjectTerrainModelsResponse> {
    return (await this.axios.get(`/projects/${params.path.projectId}/terrain-models`)).data;
  }

  registerGeodataNotificationUrl(
    params: types.RegisterGeodataNotificationUrlParameters,
  ): Promise<types.RegisterGeodataNotificationUrlResponse> {
    throw new Error('Method not implemented.');
  }

  async getSharedView(
    params: types.GetSharedViewParameters,
    signal?: AbortSignal,
  ): Promise<common.FetchSharedViewResponse> {
    const { data } = await this.axios.get(`/projects/${params.path.projectId}/shared-view`, {
      signal,
    });

    return data;
  }
  async getById(params: types.GetByIdParameters): Promise<common.FindProjectResponse> {
    return (await this.axios.get(`/projects/find?projectId=${params.query.projectId}`)).data;
  }
  async removeUserFromProject(
    params: types.RemoveUserFromProjectParameters,
  ): Promise<common.MessageOnlyResponse> {
    return (
      await this.axios.delete(`/projects/${params.path.projectId}/users/${params.path.userId}`)
    ).data;
  }

  async removeUserInvitationsFromProject(
    params: types.RemoveUserInvitationsFromProjectParameters,
  ): Promise<common.MessageOnlyResponse> {
    return (
      await this.axios.delete(
        `/projects/${params.path.projectId}/invitations/${params.path.invitationId}`,
      )
    ).data;
  }
  async mailParticipants(
    params: types.MailParticipantsParameters,
  ): Promise<common.MessageOnlyResponse> {
    const { data } = await this.axios.post<common.MessageOnlyResponse>(
      '/projects/mailparticipants',
      params.body,
    );

    return data;
  }

  async addUsersToProjects(
    params: types.AddUsersToProjectsParameters,
  ): Promise<types.AddUsersToProjectsResponse> {
    const result = await this.axios.post<types.AddUsersToProjectsResponse>(
      `/projects/addprojectusers`,
      params.body,
      { headers: { ...params.headers } },
    );
    return result.data;
  }
  async getProjectUsers(
    params: types.GetProjectUsersParameters,
  ): Promise<types.GetProjectUsersResponse> {
    return (await this.axios.get(`/projects/projectusers`)).data;
  }
  getPlatformSettings(
    params: types.GetPlatformSettingsParameters,
  ): Promise<types.PlatformSettingsResponse> {
    throw new Error('Method not implemented.');
  }
  orgNumberExists(params: types.OrgNumberExistsParameters): Promise<types.OrgnumberExistsResponse> {
    throw new Error('Method not implemented.');
  }
  async getProjectTransferRequest(
    params: types.GetProjectTransferRequestParameters,
  ): Promise<types.GetProjectTransferResponse> {
    const { data } = await this.axios.get<types.GetProjectTransferResponse>(
      `/projects/projecttransferrequest`,
      { params: params.query },
    );
    return data;
  }
  async executeProjectTransferRequest(
    params: types.ExecuteProjectTransferRequestParameters,
  ): Promise<common.MessageOnlyResponse> {
    const { data } = await this.axios.put<common.MessageOnlyResponse>(
      `/projects/projecttransferrequest`,
      undefined,
      { params: params.query },
    );
    return data;
  }
  async sendProjectTransferRequest(
    params: types.SendProjectTransferRequestParameters,
  ): Promise<common.MessageOnlyResponse> {
    const { data } = await this.axios.post<common.MessageOnlyResponse>(
      '/projects/projecttransferrequest',
      params.body,
    );
    return data;
  }
  async fetchTimelineContainer(
    params: types.FetchTimelineContainerParameters,
  ): Promise<types.FetchTimelineContainersResponse> {
    const { data } = await this.axios.get<types.FetchTimelineContainersResponse>(
      `/projects/${params.path.projectId}/timelines`,
    );

    return data;
  }

  async createTimelineContainer(
    params: types.CreateTimelineContainerParameters,
  ): Promise<types.CreateTimelineContainerResponse> {
    const { data } = await this.axios.post<types.CreateTimelineContainerResponse>(
      `/projects/${params.path.projectId}/timelines`,
      params.body,
    );

    return data;
  }
  removeAssetDynamic(
    params: types.RemoveAssetDynamicParameters,
  ): Promise<types.SingleProjectResponse> {
    throw new Error('Method not implemented.');
  }
  removeModel(params: types.RemoveModelParameters): Promise<types.SingleProjectResponse> {
    throw new Error('Method not implemented.');
  }
  async getCadFilesV1(params: types.GetCadFilesV1Parameters): Promise<types.GetCadFilesResponse> {
    return (await this.axios.get(`/projects/${params.path.projectId}/cad-files-v1`)).data;
  }
}
